import { useState } from 'react';
import { Box, Container as MUIContainer, useTheme, IconButton, Link } from '@mui/material';

import { getParsedNamedSpacing, MaxWidth } from '@widget/toolbox/style/theme/common/spacings';
import { DLProvider } from '@widget/core/DataLayerContext';
import { Icon } from '@widget/toolbox/components';
import { Navigation } from './Navigation';
import { Sidebar } from './Sidebar';
import { Logo } from '@toolbox/components/HeaderCommon/Logo';
import ThemeProvider from '@toolbox/style/theme/common/ThemeProvider/ThemeProvider';
import { PhoneCTA } from './PhoneCTA';
import { pushGAEvent } from '@widget/analytics';
import { CallBackBlock } from '@toolbox/components/HeaderCommon/CallBackBlock/CallBackBlock';

import type { CallBackBlockType } from '@toolbox/components/HeaderCommon/CallBackBlock/CallBackBlock';
import type { LogoType } from '@toolbox/components/HeaderCommon/Logo';
import type { NavItem } from './Navigation';
import type { FC } from 'react';
import type { PhoneCta } from '@widget/types/PhoneCtaType';

export type HeaderProps = {
  logos?: LogoType[];
  navItems: NavItem[];
  phoneCTA?: PhoneCta | null;
  hideNavigation: boolean;
  loginLink?: string | null;
  callbackCta?: CallBackBlockType | null;
};

export const Header: FC<HeaderProps> = (props) => {
  return (
    <ThemeProvider>
      <HeaderInner {...props} />
    </ThemeProvider>
  );
};

const HeaderInner: FC<HeaderProps> = ({
  phoneCTA,
  hideNavigation,
  navItems,
  logos,
  loginLink,
  callbackCta,
}) => {
  const theme = useTheme();
  const [showSidebar, setShowBar] = useState(false);
  const toggleSidebar = (): void => setShowBar((showSidebar) => !showSidebar);

  return (
    <Box
      display="flex"
      alignItems="center"
      position="sticky"
      top="0"
      zIndex={9999}
      sx={{
        backgroundImage: 'linear-gradient(white, rgb(248, 248, 248) 100px);',
        backgroundAttachment: 'fixed',
        backgroundSize: 'cover',
        backgroundColor: theme.palette.grey['50'],
        color: theme.palette.primary.main,
      }}
      height={{
        xs: getParsedNamedSpacing('headerHeightXs'),
        sm: getParsedNamedSpacing('headerHeightSm'),
        md: getParsedNamedSpacing('headerHeightMd'),
        lg: getParsedNamedSpacing('headerHeightLg'),
        xl: getParsedNamedSpacing('headerHeightXl'),
      }}
      component="header"
    >
      <DLProvider value={{ componentType: 'Header' }}>
        <MUIContainer
          sx={{
            px: 2,
            width: '100%',
            [theme.breakpoints.up('xl')]: {
              maxWidth: `${MaxWidth}px`,
            },
            [theme.breakpoints.up('lg')]: {
              maxWidth: '100%',
              px: 5,
            },
          }}
        >
          <Box display="flex" alignItems="center">
            {logos?.length && logos.map((logo) => <Logo logo={logo} key={logo.image.filename} />)}
            {!hideNavigation && (
              <Box display={{ xs: 'none', xl: 'flex' }}>
                <Navigation data-position="header" navItems={navItems} isMobileNavigation={false} />
              </Box>
            )}

            <Box ml="auto" display="flex">
              {!!callbackCta && <CallBackBlock {...callbackCta} />}
              {!!phoneCTA && <PhoneCTA phoneCTA={phoneCTA} />}
              {!!loginLink && (
                <Link
                  href={loginLink}
                  target={'_blank'}
                  underline="none"
                  sx={{
                    width: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 0 0 8px',
                    fontSize: '24px',
                  }}
                  onClick={() => {
                    pushGAEvent('navigation_click', {
                      label: 'portal',
                      info: 'redirect to portal',
                    });
                  }}
                >
                  <Icon type="Account" htmlColor={theme.palette.primary.main} size={'inherit'} />
                </Link>
              )}
              {!hideNavigation && (
                <>
                  <Box display={{ xs: 'flex', xl: 'none', marginLeft: '8px' }}>
                    <IconButton
                      disableRipple={true}
                      aria-label="open sidebar"
                      onClick={toggleSidebar}
                      aria-expanded={showSidebar}
                    >
                      <Icon type="Menu" size={'inherit'} htmlColor={theme.palette.primary.main} />
                    </IconButton>
                  </Box>
                  <Sidebar
                    showSidebar={showSidebar}
                    logos={logos}
                    toggleSidebar={toggleSidebar}
                    phoneCTA={phoneCTA}
                    navItems={navItems}
                  />
                </>
              )}
            </Box>
          </Box>
        </MUIContainer>
      </DLProvider>
    </Box>
  );
};
